import './Form.css'

import type { FormEvent, ReactNode } from 'react'
import type { FormValidator } from './FormValidators'
import type { FormChangeHandler, FormFlagHandler, FormSubmitHandler } from './FormContext'

import { useMemo, useRef } from 'react'

import { useRafEffect } from '../hooks/useRafEffect'
import { cx } from '../utilities/cx'
import { FormContext, useFormReducer } from './FormContext'

export type FormProps<V extends Record<string, any>> = {
  className?: string
  value?: V
  flags?: Record<string, string[]>
  erroringFlags?: Record<string, string[]>
  errorsAfterSubmit?: boolean
  validate?: FormValidator<V> | FormValidator<V>[]
  onChange?: FormChangeHandler<V>
  onSubmit?: FormSubmitHandler
  onFlag?: FormFlagHandler
  children?: ReactNode
}

export const Form = <V extends Record<string, any>>(props: FormProps<V>) => {
  const {
    className,
    value,
    flags: providedFlags,
    erroringFlags: erroringFormFlags = {},
    errorsAfterSubmit: showErrorFlagsAfterSubmit = true,
    validate: formValidatorOrValidators,
    onChange: formChangeHandler,
    onSubmit: formSubmitHandler,
    onFlag: formFlagHandler,
    children,
  } = props

  const formValidatorsMemo = () =>
    typeof formValidatorOrValidators === 'function'
      ? [formValidatorOrValidators]
      : (formValidatorOrValidators ?? [])
  const formValidators = useMemo(formValidatorsMemo, [formValidatorOrValidators])

  const formChangeHandlerRef = useRef<FormChangeHandler<V>>(() => {})
  const formFlagHandlerRef = useRef<FormFlagHandler>(() => {})
  const formSubmitHandlerRef = useRef<FormSubmitHandler>(() => {})

  formChangeHandlerRef.current = (value: Record<string, any>) => formChangeHandler?.(value as V)
  formFlagHandlerRef.current = (flags, showErrors) => formFlagHandler?.(flags, showErrors)
  formSubmitHandlerRef.current = () => formSubmitHandler?.()

  const [formState, dispatch] = useFormReducer({
    belongsToForm: true,
    value,
    providedFlags,
    erroringFormFlags,
    showErrorFlagsAfterSubmit,
    formChangeHandlerRef,
    formFlagHandlerRef,
    formSubmitHandlerRef,
  } as any)

  const validateEffect = () => {
    dispatch({ type: 'runFormValidator', formValidators: formValidators as any })
  }

  const onFlagEffect = () => {
    dispatch({ type: 'maybeCallFlagHandler' })
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    dispatch({ type: 'trySubmit' })
  }

  useRafEffect(validateEffect, [dispatch, formValidators, value])
  useRafEffect(onFlagEffect, [dispatch, formState.formFlags, formState.fieldFlags])

  return (
    <form className={cx('form', className)} onSubmit={handleSubmit}>
      <FormContext.Provider value={[formState, dispatch]}>{children}</FormContext.Provider>
      <input type="submit" hidden />
    </form>
  )
}
